import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, View, ActivityIndicator, TouchableOpacity, Switch, Image } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import MyRadio from '../../../components/MyRadio';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faInfoCircle, faUpRightFromSquare, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import DisableEnableModal from './DisableEnableModal';
import Tooltip from '../../../components/Tooltip';
import Toast from 'react-native-toast-message';
import { RouteProp, useFocusEffect, useIsFocused, useNavigationState, useRoute } from '@react-navigation/native';
import { DuplicatedCameraValue, crmService } from '../../../services/central-api/crm';
import { useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import PasswordInput from '../../../components/PasswordInput';
import { ClientError, InvalidValue } from '../../../services/central-api/base-service';
import CustomButton from '../../../components/CustomButton';
import AlertGroupsDropdown from '../../../components/Dropdowns/AlertGroupsDropdown';
import PasswordModal from './PasswordModal';
import { useAuth } from '../../../context/Auth';
import InstallationCompanyDropdown from './installationCompanyDropdown';
import InternetCompanyDropdown from './InternetCompanyDropdown';
import Map from '../../../components/Map';
import { WindowInformation } from '../../../services/window-information';
import LocationSelector from '../../../components/LocationSelector';
import { centralAPI } from '../../../services/central-api';
import CameraManufacturerDropdown from '../../../components/crm/CameraManufacturerDropdown';
import VideoStorageTimeDropdown from '../../../components/crm/VideoStorageDropdown';
import CameraStorageDropdown from '../../../components/crm/CameraStorageDropdown';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { useForm, useWatch } from 'react-hook-form';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import AreaInvasionModal from './AreaInvasionModal';
import { CameraType, StorageTypeFilter } from '../../../schemas/cameras';
import { Manufacturer } from '../../../schemas/crm';
import { useCurrentApp } from '../../../context/CurrentApp';
import CameraTypeDropdown from '../../../components/Filters/Cameras/CameraTypeDropdown';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { CRMParamList } from '../../../typings/Params';
import { Tabs } from '../../../components/Tabs';
import SynchronizationsTab from './SynchronizationsTab';
import SynchronizationSettingsTab, {
    buildSynchronizationConfigurationFormValues,
    buildSynchronizationConfigurationPayload,
    DEFAULT_SYNCHRONIZATION_CONFIGURATION,
    getDefaultSensitivities,
    isSynchronizationConfigurationValid,
    SynchronizationConfigurationFormFields,
} from './SynchronizationSettingsTab';
import { RootNavigation } from '../../../Routes';
import { openInNewTab } from '../../../services/navigation-assistant';
import AlertModal from '../../../components/AlertModal';
import { TranslateRich } from '../../../components/TranslateRich';
import FontWeight from '../../../styles/fontWeights';
import ImageAlgorithmsTab, { ImageAlgorithmsForm } from './ImageAlgorithmsTab';
import PurgeCameraModal from './PurgeCameraModal';

const DEFAULT_FORM_VALUES: CameraFormFields = {
    title: '',
    address: '',
    httpAddress: undefined,
    serialNo: null,
    type: 'pinned',
    internetType: '4g',
    installationCompanyId: 0,
    internetCompanyId: 0,
    alertGroupId: null,
    rtspAddress: null,
    installer: '',
    installedAmountCameras: 0,
    networkType: 'ipv6',
    sourceTech: 'rtsp',
    hasLpr: false,
    hasFacial: false,
    hasAreaInvasion: false,
    databaseHasAreaInvasion: false,
    riskArea: false,
    macNumber: '',
    chipNumber: '',
    shelterId: '',
    stolen: false,
    ptzConfiguration: undefined,
    manufacturer: 'hikvision',
    videoStorageTime: '15',
    storageType: 'cloud',
    synchronizationConfiguration: {
        ...DEFAULT_SYNCHRONIZATION_CONFIGURATION,
        ...getDefaultSensitivities('hikvision'),
    },
    companyId: undefined,
    disabledAt: null,
    isThirdParty: false,
    isDonated: false,
};

interface CameraFormFields {
    title: string;
    address: string;
    httpAddress: string | undefined | null;
    serialNo: string | null;
    type: CameraType;
    internetType: InternetType;
    installationCompanyId: number | undefined;
    internetCompanyId: number | undefined;
    alertGroupId: number | null;
    rtspAddress: string | null;
    installer: string;
    installedAmountCameras: number;
    networkType: NetworkType;
    sourceTech: SourceTech;
    hasLpr: boolean;
    hasFacial: boolean;
    hasAreaInvasion: boolean;
    databaseHasAreaInvasion: boolean;
    riskArea: boolean;
    macNumber: string;
    chipNumber: string;
    shelterId: string;
    stolen: boolean;
    ptzConfiguration?: PtzConfiguration;
    manufacturer: Manufacturer;
    videoStorageTime: VideoStorageTime;
    storageType: StorageTypeFilter;
    synchronizationConfiguration: SynchronizationConfigurationFormFields;
    companyId?: number;
    disabledAt: Date | null;
    isThirdParty: boolean;
    isDonated: boolean;
}

export default function ManagementDetails({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<CRMParamList, 'ManagementDetails'>>();

    const { theme } = useStyles();
    const { user: authenticatedUser } = useAuth();
    const { isParentApp } = useCurrentApp();
    const windowInfo = WindowInformation();

    const [showStolenModal, setShowStolenModal] = useState(false);
    const [isFactoryResetVisible, setIsFactorResetVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordModalVisible, setPasswordModalVisible] = useState(false);
    const [isDisableEnableModalVisible, setDisableEnableModalVisible] = useState(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isForceSynchronizationModalVisible, setForceSynchronizationModalVisible] = useState(false);
    const [pin, setPin] = useState<LatLng | undefined>();
    const [cameraPassword, setCameraPassword] = useState('');
    const [cameraMetadata, setCameraMetadata] = useState<CameraMetadata | null>(null);
    const [companyDefaults, setCompanyDefaults] = useState<UpdateCompanyCameraConfigurationParameters>();
    const loadedManufacturer = useRef<Manufacturer>();
    const [purgeCameraModalVisible, setPurgeCameraModalVisible] = useState(false);

    const index = useNavigationState(state => state);
    const [isDrawingAreaModalVisible, setIsDrawingAreaModalVisible] = useState(false);

    const form = useForm<CameraFormFields>({
        defaultValues: DEFAULT_FORM_VALUES,
        mode: 'onChange',
    });

    const networkType = useWatch({ control: form.control, name: 'networkType' });
    const internetType = useWatch({ control: form.control, name: 'internetType' });
    const cameraType = useWatch({ control: form.control, name: 'type' });
    const manufacturer = useWatch({ control: form.control, name: 'manufacturer' });
    const companyId = useWatch({ control: form.control, name: 'companyId' });
    const hasLpr = useWatch({ control: form.control, name: 'hasLpr' });
    const hasFacial = useWatch({ control: form.control, name: 'hasFacial' });
    const hasAreaInvasion = useWatch({ control: form.control, name: 'hasAreaInvasion' });
    const riskArea = useWatch({ control: form.control, name: 'riskArea' });
    const stolen = useWatch({ control: form.control, name: 'stolen' });
    const disabledAt = useWatch({ control: form.control, name: 'disabledAt' });
    const isThirdParty = useWatch({ control: form.control, name: 'isThirdParty' });
    const isDonated = useWatch({ control: form.control, name: 'isDonated' });
    const httpAddress = useWatch({ control: form.control, name: 'httpAddress' });
    const rtspAddress = useWatch({ control: form.control, name: 'rtspAddress' });
    const installedAmountCameras = useWatch({ control: form.control, name: 'installedAmountCameras' });
    const ptzConfiguration = useWatch({ control: form.control, name: 'ptzConfiguration' });
    const alertGroupId = useWatch({ control: form.control, name: 'alertGroupId' });
    const synchronizationConfiguration = useWatch({ control: form.control, name: 'synchronizationConfiguration' });
    const title = useWatch({ control: form.control, name: 'title' });
    const serialNo = useWatch({ control: form.control, name: 'serialNo' });

    const canEditFields = useMemo(
        () => !!(authenticatedUser?.isAdmin || authenticatedUser?.permissions.crm_module_access),
        [authenticatedUser]
    );

    const isFormValid = useMemo(() => {
        if (!installedAmountCameras || installedAmountCameras < 1) {
            return false;
        }
        if (networkType === 'ipv4') {
            if (!rtspAddress) {
                return false;
            }
            if (!httpAddress) {
                return false;
            }
        }

        if (!route.params?.id && !serialNo) {
            return false;
        }

        if (cameraType === 'ptz') {
            if (!ptzConfiguration?.username) {
                return false;
            }
            if (!ptzConfiguration?.password) {
                return false;
            }
        }
        if (hasAreaInvasion && !alertGroupId) {
            return false;
        }
        if (pin && (Number.isNaN(pin.lat) || Number.isNaN(pin.lng))) {
            return false;
        }

        if (isParentApp && !isSynchronizationConfigurationValid(synchronizationConfiguration as SynchronizationConfigurationFormFields, manufacturer)) {
            return false;
        }

        return true;
    }, [installedAmountCameras, networkType, rtspAddress, httpAddress, cameraType, ptzConfiguration, hasAreaInvasion, alertGroupId, pin, serialNo, isParentApp, synchronizationConfiguration, manufacturer]);

    const goBack = useCallback(() => {
        if (index.routes.length > 1) {
            navigation.goBack();
        } else {
            navigation.navigate('CRM', { screen: 'Management' });
        }
    }, [index.routes.length]);

    useEffect(() => {
        if (cameraType == 'lpr') {
            form.setValue('hasLpr', true);
            form.setValue('hasFacial', false);
            form.setValue('hasAreaInvasion', false);
            form.setValue('alertGroupId', null);
        }
    }, [cameraType]);

    function getDefaultSynchronizationConfiguration(manufacturer: Manufacturer): SynchronizationConfigurationFormFields {
        return {
            ...DEFAULT_SYNCHRONIZATION_CONFIGURATION,
            ...getDefaultSensitivities(manufacturer),
        };
    }

    async function fetchAddress(pos: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pos.lat, longitude: pos.lng });
        if (address) {
            form.setValue('address', address);
        }
    }

    async function getSynchronizationConfiguration(id: string, manufacturer: Manufacturer): Promise<SynchronizationConfigurationFormFields> {
        if (!isParentApp) {
            return getDefaultSynchronizationConfiguration(manufacturer);
        }

        try {
            const configuration = await crmService.getCameraSynchronizationConfiguration(id);
            setCompanyDefaults(configuration.companyDefaults);
            return buildSynchronizationConfigurationFormValues(configuration, manufacturer);
        } catch (err) {
            console.error(err);
            setCompanyDefaults(undefined);
            Toast.show({ type: 'sentinelxError', text1: translate('failedToLoadSynchronizationSettings') });
            return getDefaultSynchronizationConfiguration(manufacturer);
        }
    }

    const cameraShotServiceForm = useForm<ImageAlgorithmsForm>({
        defaultValues: {
            cameraId: '',
            services: {
                raining: false,
                person_detection: false,
                people_crowd: false,
            },
            times: {},
        },
    });

    async function getCameraShotServices(id: string) {
        if (!isParentApp) {
            return getDefaultSynchronizationConfiguration(manufacturer);
        }

        const cameraShotService = await crmService.getCameraShotsService(id);

        cameraShotServiceForm.reset({
            cameraId: cameraShotService.cameraId,
            services: {
                raining: cameraShotService.service === 'raining',
                person_detection: cameraShotService.service === 'person_detection',
                people_crowd: cameraShotService.service === 'people_crowd',
            },
            times: cameraShotService.times ?? {},
        });
    }

    async function getCamera(id: string) {
        try {
            setIsLoading(true);
            const camera = await crmService.getCamera(id);
            fetchAddress({ lat: camera.latitude, lng: camera.longitude });
            setPin({ lat: camera.latitude, lng: camera.longitude });
            setCameraMetadata(camera.cameraMetadata ?? null);

            const isThirdParty = !!(camera.thirdPartyCamera || camera.originalCamera);
            const isDonated = !!camera.originalCameraId;
            const synchronizationConfiguration = isThirdParty || isDonated
                ? getDefaultSynchronizationConfiguration(camera.manufacturer)
                : await getSynchronizationConfiguration(id, camera.manufacturer);

            await getCameraShotServices(id);

            loadedManufacturer.current = camera.manufacturer;

            form.reset({
                title: camera.title,
                address: camera.address,
                httpAddress: camera.httpAddress,
                serialNo: camera.serialNo,
                type: camera.type,
                internetType: camera.internetType,
                installationCompanyId: camera.installationCompanyId ?? 0,
                internetCompanyId: camera.internetCompanyId ?? 0,
                alertGroupId: camera.alertGroupId,
                rtspAddress: camera.rtspAddress,
                installer: camera.installer,
                installedAmountCameras: camera.installedAmountCameras,
                networkType: camera.networkType,
                sourceTech: camera.sourceTech,
                hasLpr: camera.hasLpr,
                hasFacial: camera.hasFacial,
                hasAreaInvasion: camera.hasAreaInvasion,
                databaseHasAreaInvasion: camera.hasAreaInvasion,
                macNumber: camera.macNumber ?? '',
                chipNumber: camera.chipNumber ?? '',
                shelterId: camera.shelterId ?? '',
                stolen: camera.stolen,
                ptzConfiguration: camera.ptzConfiguration,
                manufacturer: camera.manufacturer,
                videoStorageTime: camera.videoStorageTime,
                storageType: camera.storageType,
                synchronizationConfiguration,
                isThirdParty,
                isDonated,
                companyId: camera.companyId,
                riskArea: camera.riskArea,
                disabledAt: camera.disabledAt
            });
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (route.params?.id) {
            getCamera(route.params?.id);
        }
    }, [isFocused, navigation, route]));

    useEffect(() => {
        if (!manufacturer || loadedManufacturer.current === manufacturer) {
            return;
        }

        const previousManufacturer = loadedManufacturer.current;
        loadedManufacturer.current = manufacturer;

        const defaults = getDefaultSensitivities(manufacturer);
        form.setValue('synchronizationConfiguration.videoTamperingSensitivity', defaults.videoTamperingSensitivity, { shouldDirty: true });
        form.setValue('synchronizationConfiguration.sceneChangeSensitivity', defaults.sceneChangeSensitivity, { shouldDirty: true });
        form.setValue('synchronizationConfiguration.defocusSensitivity', defaults.defocusSensitivity, { shouldDirty: true });

        if (previousManufacturer !== undefined && isParentApp && route.params?.id) {
            Toast.show({
                type: 'sentinelxWarning',
                text1: translate('manufacturerChangedResetsSensitivities'),
                visibilityTime: 8000,
            });
        }
    }, [manufacturer]);

    function buildPtzConfiguration(data: CameraFormFields): PtzConfiguration | undefined {
        if (data.type !== 'ptz') {
            return undefined;
        }
        return {
            username: data.ptzConfiguration?.username ?? '',
            password: data.ptzConfiguration?.password ?? '',
        };
    }

    function showSaveError(err: unknown, fallbackMessage: string) {
        if (err instanceof DuplicatedCameraValue) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate(err.message, { cameraId: err.cameraId }),
                visibilityTime: 10000,
            });
        } else if (err instanceof InvalidValue) {
            Toast.show({ type: 'sentinelxError', text1: translate(fallbackMessage), text2: translate(err.message) });
        } else if (err instanceof ClientError) {
            Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
        } else if (err instanceof TypeError) {
            Toast.show({ type: 'sentinelxError', text1: translate('InvalidHttpAddress') });
        } else {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate(fallbackMessage) });
        }
    }

    async function saveCamera(data: CameraFormFields): Promise<string | undefined> {
        const resolvedRtspAddress = data.networkType === 'ipv4' && data.sourceTech === 'rtsp' && data.rtspAddress
            ? data.rtspAddress
            : undefined;
        const resolvedHttpAddress = data.networkType === 'ipv4' && data.httpAddress
            ? data.httpAddress
            : undefined;

        const sharedPayload = {
            title: data.title,
            address: data.address,
            latitude: Number(pin?.lat),
            longitude: Number(pin?.lng),
            hasFacial: data.hasFacial,
            hasLpr: data.hasLpr,
            hasAreaInvasion: data.hasAreaInvasion,
            internetType: data.internetType,
            installer: data.installer,
            installationCompanyId: data.installationCompanyId,
            internetCompanyId: data.internetCompanyId,
            alertGroupId: data.alertGroupId ?? null,
            sourceTech: data.sourceTech,
            rtspAddress: resolvedRtspAddress,
            httpAddress: resolvedHttpAddress,
            networkType: data.networkType,
            installedAmountCameras: Number(data.installedAmountCameras),
            ptzConfiguration: buildPtzConfiguration(data),
            riskArea: data.riskArea,
            macNumber: data.macNumber,
            chipNumber: data.chipNumber,
            shelterId: data.shelterId,
            manufacturer: data.manufacturer,
            videoStorageTime: data.videoStorageTime,
            storageType: data.storageType,
        };

        if (route.params?.id) {
            await crmService.updateCamera(route.params?.id, {
                ...sharedPayload,
                serialNo: data.serialNo ?? undefined,
                stolen: data.stolen,
            });
            return route.params.id;
        }

        if (data.serialNo && sharedPayload.address) {
            const camera = await crmService.createCamera({
                ...sharedPayload,
                type: data.type,
                serialNo: data.serialNo,
                stolen: false,
                internetCompanyId: data.internetCompanyId ?? 0,
                installationCompanyId: data.installationCompanyId ?? 0,
                cameraConfiguration: {
                    videoTamperingSensitivity: data.synchronizationConfiguration.videoTamperingSensitivity,
                    sceneChangeSensitivity: data.synchronizationConfiguration.sceneChangeSensitivity,
                    defocusSensitivity: data.synchronizationConfiguration.defocusSensitivity,
                },
            });
            return camera?.id;
        }

        return undefined;
    }

    async function saveSynchronizationConfiguration(cameraId: string, data: CameraFormFields) {
        await crmService.updateCameraSynchronizationConfiguration(
            cameraId,
            buildSynchronizationConfigurationPayload(data.synchronizationConfiguration, data.manufacturer)
        );
    }

    async function saveCameraShotServices(cameraId: string) {
        const data = cameraShotServiceForm.getValues();

        const enabledService = Object.entries(data.services).find(([, enabled]) => enabled)?.[0] as CameraShotServices | undefined;
        await crmService.updateCameraShotService(cameraId, enabledService ? { service: enabledService, times: data.times } : {});
    }

    async function save() {
        const data = form.getValues();
        setIsLoading(true);

        try {
            let cameraId: string | undefined;

            try {
                cameraId = await saveCamera(data);
            } catch (err) {
                showSaveError(err, 'unexpectedError');
                return;
            }

            const shouldSaveParentDependencies = isParentApp && !!route.params?.id && !data.isThirdParty && !data.isDonated;

            if (shouldSaveParentDependencies && cameraId) {
                try {
                    await saveSynchronizationConfiguration(cameraId, data);
                } catch (err) {
                    showSaveError(err, 'configurationUpdateFailed');
                    await getCamera(cameraId);
                    return;
                }
            }

            if (shouldSaveParentDependencies && cameraId) {
                try {
                    await saveCameraShotServices(cameraId);
                } catch (err) {
                    showSaveError(err, 'shotServiceUpdateFailed');
                    await getCamera(cameraId);
                    return;
                }
            }

            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            goBack();
        } finally {
            setIsLoading(false);
        }
    }

    async function getCameraPassword() {
        try {
            setPasswordModalVisible(true);
            const result = await crmService.getCameraPassword(route.params?.id ?? '');
            setCameraPassword(result.password);
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    const dropDownOptions = useMemo<Action[]>(() => {
        const actions: Action[] = [];
        const cameraId = route.params?.id;
        if (!cameraId) {
            return [];
        }

        actions.push({
            content: translate('watchCamera'),
            onPress: () => navigation.navigate('Cameras', { screen: 'CameraPlayer', params: { id: cameraId } }),
        }, {
            content: translate('watchCameraOnNewTab'),
            onPress: () => {
                openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: cameraId } });
            }
        });

        if (authenticatedUser?.permissions.get_camera_password || authenticatedUser?.isAdmin) {
            actions.push({ content: translate('getPassword'), onPress: getCameraPassword });
        }

        if (hasLpr && (authenticatedUser?.isAdmin || authenticatedUser?.permissions.lpr_module_access)) {
            actions.push({
                content: translate('watchLprEvents'),
                onPress: () => navigation.navigate('LPR', { screen: 'LprLiveEvents', params: { cameras: [cameraId] } }),
            });
        }

        if (hasFacial && (
            authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.facial_module_access
            || authenticatedUser?.permissions.facial_events_missing_person
            || authenticatedUser?.permissions.facial_events_wanted_person
        )) {
            actions.push({
                content: translate('watchFacialEvents'),
                onPress: () => navigation.navigate('Facial', { screen: 'LiveEvents', params: { cameras: [cameraId] } }),
            });
        }

        if (isParentApp && canEditFields) {
            actions.push({
                content: translate('forceSynchronization'),
                onPress: () => setForceSynchronizationModalVisible(true),
            });
            actions.push({
                content: translate('moveCamera'),
                onPress: () => {
                    setPurgeCameraModalVisible(true);
                },
                variant: 'warning'
            });
        }

        if (canEditFields) {
            actions.push({
                content: translate('informFactoryReset'),
                onPress: () => setIsFactorResetVisible(true),
                variant: 'warning'
            }, {
                content: disabledAt !== null ? translate('enable') : translate('disable'),
                onPress: () => setDisableEnableModalVisible(true),
                variant: disabledAt !== null ? 'success' : 'danger',
            }, {
                content: translate('delete'),
                onPress: () => setDeleteModalVisible(true),
                variant: 'danger'
            });
        }

        return actions;
    }, [route.params?.id, canEditFields, authenticatedUser, hasLpr, hasFacial, disabledAt]);

    if (isThirdParty) {
        return (
            <Container>
                <Header title={translate('camera')} goBackAction={goBack} />
                <Card>
                    <MyAppText style={{ fontSize: 12 }}>{translate('thirdPartyCameraWarning')}</MyAppText>
                </Card>
            </Container>
        );
    }

    return (
        <Container>
            <Header
                title={translate('camera')}
                goBackAction={goBack}
                actions={
                    <>
                        {dropDownOptions.length > 0 && (
                            <DropdownButton
                                buttonLabel='actions'
                                buttonIcon={faChevronDown}
                                isButton
                                actions={dropDownOptions}
                                offsetX={-97}
                                offsetY={41}
                                size={200}
                            />
                        )}
                        {canEditFields && (
                            <CustomButton
                                text={translate('save')}
                                disabled={!isFormValid}
                                onPress={() => {
                                    if (form.getFieldState('stolen').isDirty && form.getValues().stolen && disabledAt === null) {
                                        setShowStolenModal(true);
                                    } else {
                                        save();
                                    }
                                }}
                            />
                        )}
                    </>
                }
            />
            <Card>
                <Tabs
                    tabs={[{
                        key: 'general_info',
                        label: translate('general_info'),
                    }, {
                        key: 'image_algorithms',
                        label: translate('imageAlgorithms'),
                        omit: !isParentApp || !route.params?.id || isThirdParty || isDonated
                    }, {
                        key: 'synchronization_settings',
                        label: translate('synchronizationSettings'),
                        omit: !isParentApp || !route.params?.id || isThirdParty || isDonated
                    }, {
                        key: 'synchronizations',
                        label: translate('synchronizations'),
                        omit: !isParentApp || !route.params?.id || isThirdParty || isDonated
                    }]}>
                    {({ selectedTab }) => {
                        if (selectedTab == 'general_info') {
                            return <ScrollView style={{ padding: 24, height: '100%' }} contentContainerStyle={{ rowGap: 16, height: '100%' }}>
                                {isLoading && <ActivityIndicator size='small' color={theme.colors.iconColor} />}

                                <View style={{ flex: 1, flexWrap: 'wrap', flexDirection: 'row', columnGap: 40, rowGap: 10, height: '100%' }}>
                                    <View style={[!windowInfo.isMobile && { flex: 1 }, { rowGap: 16, maxWidth: 400, width: '100%', zIndex: 11 }]}>
                                        <FormInput
                                            label={translate('title')}
                                            placeholder=''
                                            form={form}
                                            fieldName='title'
                                            disabled={!canEditFields}
                                            required
                                        />
                                        <LocationSelector
                                            label={translate('address')}
                                            address={form.watch('address')}
                                            onAddressChange={(address) => form.setValue('address', address.toString())}
                                            onPinChange={pos => setPin(pos)}
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            viewStyle={{ zIndex: 10 }}
                                            required
                                        />
                                        <CameraManufacturerDropdown
                                            form={form}
                                            fieldName='manufacturer'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            zIndex={9}
                                            required
                                        />
                                        <VideoStorageTimeDropdown
                                            form={form}
                                            fieldName='videoStorageTime'
                                            disabled={!isParentApp}
                                            zIndex={8}
                                            labelFontSize={14}
                                            required
                                        />
                                        {isParentApp && <CameraStorageDropdown
                                            form={form}
                                            fieldName='storageType'
                                            disabled={false}
                                            zIndex={7}
                                            labelFontSize={14}
                                            required
                                        />}
                                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                            <View style={{ width: '95%' }}>
                                                <FormInput
                                                    label={translate('serialNo')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='serialNo'
                                                    disabled={!canEditFields}
                                                    maxLength={9}
                                                    required
                                                />
                                            </View>
                                            <View style={{ paddingTop: 24 }}>
                                                <Tooltip
                                                    tooltipDirection='right'
                                                    style={{ width: 600 }}
                                                    tooltipContent={
                                                        <View style={{ flexDirection: 'column', rowGap: 10, alignItems: 'center', paddingVertical: 5 }}>
                                                            <MyAppText style={{ color: theme.colors.labelColor }}>{translate('hikSerialNoExplanation')}</MyAppText>
                                                            <Image
                                                                source={require('../../../../assets/hikvision_serial_no.png')}
                                                                style={{ height: 300, width: 580, objectFit: 'contain' }}
                                                            />
                                                        </View>
                                                    }
                                                >
                                                    <FontAwesomeIcon icon={faInfoCircle} color={theme.colors.iconColor} />
                                                </Tooltip>
                                            </View>
                                        </View>

                                        <MyRadio
                                            label={translate('networkType')}
                                            required
                                            editable={canEditFields}
                                            selected={networkType}
                                            setSelected={(value: NetworkType) => form.setValue('networkType', value)}
                                            options={[{ value: 'ipv6', label: 'IPv6' }, { value: 'ipv4', label: 'IPv4' }]}
                                            labelFontSize={14}
                                        />

                                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                            <View style={cameraType === 'panoramic' ? { width: '95%' } : { width: '100%' }}>
                                                <FormInput
                                                    label={translate('rtspAddress')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='rtspAddress'
                                                    disabled={!canEditFields || networkType !== 'ipv4'}
                                                    required={networkType === 'ipv4'}
                                                />
                                            </View>
                                            {cameraType === 'panoramic' && (
                                                <View style={{ paddingTop: 24 }}>
                                                    <Tooltip
                                                        tooltipDirection='right'
                                                        style={{ width: 300 }}
                                                        tooltipContent={
                                                            <View style={{ paddingVertical: 5 }}>
                                                                <MyAppText>{translate('rtspIpv4Panoramic')}</MyAppText>
                                                            </View>
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                    </Tooltip>
                                                </View>
                                            )}
                                        </View>

                                        {(route.params?.id || networkType === 'ipv4') && (
                                            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                                <View style={route.params?.id ? { width: '95%' } : { width: '100%' }}>
                                                    <FormInput
                                                        label={translate('httpAddress')}
                                                        placeholder=''
                                                        form={form}
                                                        fieldName='httpAddress'
                                                        disabled={networkType !== 'ipv4'}
                                                        invalid={() => false}
                                                        required
                                                    />
                                                </View>
                                                {route.params?.id && (
                                                    <View style={{ paddingTop: 24 }}>
                                                        <TouchableOpacity onPress={() => {
                                                            const a = document.createElement('a');
                                                            const href = httpAddress as string;
                                                            a.href = href.includes('http') ? href : `http://${href}`;
                                                            a.target = '_blank';
                                                            a.click();
                                                        }}>
                                                            <FontAwesomeIcon icon={faUpRightFromSquare} color={theme.colors.iconColor} />
                                                        </TouchableOpacity>
                                                    </View>
                                                )}
                                            </View>
                                        )}

                                        <InstallationCompanyDropdown
                                            form={form}
                                            fieldName='installationCompanyId'
                                            zIndex={6}
                                            disabled={!authenticatedUser?.isAdmin && !!route.params?.id}
                                            companyId={isParentApp ? companyId : undefined}
                                            labelFontSize={14}
                                            required
                                        />
                                        <FormInput
                                            label={translate('installer')}
                                            placeholder=''
                                            form={form}
                                            fieldName='installer'
                                            disabled={!authenticatedUser?.isAdmin && !!route.params?.id}
                                            required
                                        />


                                        <MyRadio
                                            label={translate('internetType')}
                                            required
                                            selected={internetType}
                                            editable={canEditFields}
                                            setSelected={(value: InternetType) => {
                                                form.setValue('internetType', value);
                                            }}
                                            options={[
                                                { label: translate('fiber'), value: 'fiber' },
                                                { label: '5G', value: '5g' },
                                                { label: '4G', value: '4g' },
                                                { label: translate('radio'), value: 'radio' },
                                            ]}
                                            labelFontSize={14}
                                        />
                                    </View>
                                    <View style={[!windowInfo.isMobile && { flex: 1 }, { rowGap: 16, maxWidth: 400, width: '100%' }]}>
                                        <InternetCompanyDropdown
                                            form={form}
                                            fieldName='internetCompanyId'
                                            zIndex={6}
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            companyId={isParentApp ? companyId : undefined}
                                            required
                                        />
                                        <FormInput
                                            label={translate('installedAmountCameras')}
                                            placeholder=''
                                            form={form}
                                            fieldName='installedAmountCameras'
                                            disabled={!canEditFields}
                                            required
                                            mode='NUMBER'
                                            minValue={1}
                                        />
                                        <CameraTypeDropdown
                                            form={form}
                                            fieldName='type'
                                            includeAll={false}
                                            required={true}
                                            disabled={!!route.params?.id}
                                            zIndex={5}
                                        />
                                        <FormInput
                                            label={translate('macNumber')}
                                            placeholder=''
                                            form={form}
                                            fieldName='macNumber'
                                            disabled={!canEditFields}
                                            required={false}
                                        />
                                        {internetType === '4g' && (
                                            <FormInput
                                                label={translate('chipNumber')}
                                                placeholder=''
                                                form={form}
                                                fieldName='chipNumber'
                                                disabled={!canEditFields}
                                                required={false}
                                            />
                                        )}
                                        <FormInput
                                            label={translate('ShelterId')}
                                            placeholder=''
                                            form={form}
                                            fieldName='shelterId'
                                            disabled={!canEditFields}
                                            required={false}
                                        />

                                        <View style={{ rowGap: 4 }}>
                                            <View style={{
                                                flexDirection: 'row',
                                                gap: 4,
                                            }}>
                                                <MyAppText style={{
                                                    color: theme.colors.neutral[500],
                                                    fontSize: 14,
                                                    height: 19,
                                                    fontWeight: FontWeight.semiBold,
                                                }}>{translate('algorithm')}</MyAppText>
                                            </View>
                                            <View style={{ flexDirection: 'row', columnGap: 20 }}>
                                                {([
                                                    { key: 'hasLpr', label: translate('lpr'), value: hasLpr },
                                                    { key: 'hasFacial', label: translate('facial'), value: hasFacial },
                                                    { key: 'hasAreaInvasion', label: translate('area_invasion'), value: hasAreaInvasion },
                                                ] as const).map(({ key, label, value }) => (
                                                    <View key={key}>
                                                        <MyAppText style={{ fontSize: 14 }}>{label}</MyAppText>
                                                        <Switch
                                                            trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                            thumbColor={value ? '#f5dd4b' : '#f4f3f4'}
                                                            disabled={cameraType === 'lpr'}
                                                            onValueChange={(v) => {
                                                                if (canEditFields) {
                                                                    form.setValue('hasLpr', false);
                                                                    form.setValue('hasFacial', false);
                                                                    form.setValue('hasAreaInvasion', false);
                                                                    form.setValue(key, v);
                                                                }
                                                            }}
                                                            value={!!value}
                                                        />
                                                    </View>
                                                ))}
                                            </View>

                                            <MyAppText style={{ fontSize: 14 }}>{translate('stolen_equipment')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={stolen ? '#f5dd4b' : '#f4f3f4'}
                                                disabled={!route.params?.id}
                                                onValueChange={(v) => {
                                                    if (canEditFields || route.params?.id) {
                                                        form.setValue('stolen', v, { shouldDirty: true, shouldTouch: true });
                                                    }
                                                }}
                                                value={!!stolen}
                                            />

                                            <MyAppText style={{ fontSize: 14 }}>{translate('riskArea')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={riskArea ? '#f5dd4b' : '#f4f3f4'}
                                                onValueChange={(v) => {
                                                    if (canEditFields) {
                                                        form.setValue('riskArea', v);
                                                    }
                                                }}
                                                value={!!riskArea}
                                            />
                                        </View>

                                        {hasAreaInvasion && (
                                            <>
                                                {form.watch('databaseHasAreaInvasion') && (
                                                    <CustomButton
                                                        onPress={() => setIsDrawingAreaModalVisible(true)}
                                                        icon={faEye as IconDefinition}
                                                        text={translate('drawnArea')}
                                                    />
                                                )}
                                                <AlertGroupsDropdown form={form} fieldName='alertGroupId' zIndex={5} labelFontSize={14} required />
                                            </>
                                        )}
                                        {cameraType === 'ptz' && (
                                            <>
                                                <FormInput
                                                    label={translate('ptzUsername')}
                                                    placeholder=''
                                                    disabled={!canEditFields}
                                                    form={form}
                                                    fieldName='ptzConfiguration.username'
                                                    required
                                                />
                                                <PasswordInput
                                                    label={translate('ptzPassword')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='ptzConfiguration.password'
                                                    disabled={!canEditFields}
                                                    labelFontSize={14}
                                                    required
                                                />
                                            </>
                                        )}

                                    </View>
                                    <View style={[!windowInfo.isMobile ? { height: '100%' } : { height: 400, paddingBottom: 20 }, { flex: 1 }]}>
                                        <Map
                                            draggable={canEditFields}
                                            dynamicMarker={pin}
                                            useDynamicMarker
                                            markerChanged={(pos) => {
                                                fetchAddress(pos);
                                                setPin(pos);
                                            }}
                                        />
                                    </View>
                                </View>
                            </ScrollView>;
                        } else if (selectedTab == 'image_algorithms' && route.params.id) {
                            return <ImageAlgorithmsTab form={cameraShotServiceForm} />;
                        } else if (selectedTab == 'synchronization_settings') {
                            return <SynchronizationSettingsTab
                                form={form}
                                values={synchronizationConfiguration as SynchronizationConfigurationFormFields}
                                companyDefaults={companyDefaults}
                                manufacturer={manufacturer}
                                disabled={!canEditFields}
                                isLoading={isLoading}
                            />;
                        } else if (selectedTab == 'synchronizations') {
                            return <SynchronizationsTab metadata={cameraMetadata} isLoading={isLoading} />;
                        }
                    }}
                </Tabs>
            </Card>

            <DisableEnableModal
                isModalVisible={isDisableEnableModalVisible}
                setModalVisible={setDisableEnableModalVisible}
                cameraData={{
                    id: route.params?.id,
                    disabledAt: disabledAt,
                    title,
                }}
                setDisabledAt={(value) => form.setValue('disabledAt', value)}
            />

            <PasswordModal
                isModalVisible={isPasswordModalVisible}
                setIsModalVisible={setPasswordModalVisible}
                cameraPassword={cameraPassword}
            />

            <AlertModal
                isModalVisible={isFactoryResetVisible}
                setModalVisible={setIsFactorResetVisible}
                description={<>
                    {translate('cameraFactoryResetDescription')}
                    <br />
                    <br />
                    {translate('cameraFactoryResetProceed')}
                </>}
                onSubmit={async () => {
                    try {
                        if (!route.params?.id) {
                            return;
                        }

                        await crmService.resetFactory(route.params.id);

                        Toast.show({
                            type: 'sentinelxSuccess',
                            text1: translate('ActionSuccessfully'),
                        });

                        await getCamera(route.params.id);
                    } catch (err) {
                        if (err instanceof ClientError) {
                            return Toast.show({
                                type: 'sentinelxError',
                                text1: translate(err.message),
                            });
                        }

                        console.error(err);
                        Toast.show({
                            type: 'sentinelxError',
                            text1: translate('unexpectedError'),
                        });
                    }
                }}
                type='warning'
                customButtonLabel={translate('yesConfirm')}
                title={translate('cameraFactoryReset')}
            />

            <AlertModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                title={translate('deleteCamera')}
                customButtonLabel={translate('yesDelete')}
                type='destructive'
                description={<>
                    <TranslateRich
                        i18nKey='deleteCameraDescription'
                        values={{
                            cameraName: <strong>{title}</strong>,
                        }}
                    />
                    <br />
                    <br />
                    <TranslateRich i18nKey='proceedWithDeletion' />
                </>}
                onSubmit={async () => {
                    try {
                        if (route.params?.id) {
                            await crmService.deleteCamera(route.params?.id);
                            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
                            goBack();
                        }
                    } catch (err) {
                        if (err instanceof ClientError) {
                            Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
                        } else {
                            console.error(err);
                            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
                        }
                    }
                }}
            />

            <AlertModal
                isModalVisible={isForceSynchronizationModalVisible}
                setModalVisible={setForceSynchronizationModalVisible}
                description={translate('forceSynchronizationDescription')}
                title={translate('forceSynchronization')}
                type='warning'
                onSubmit={async () => crmService.syncCameraConfiguration(route.params.id || '')}
            />

            <AlertModal
                isModalVisible={showStolenModal}
                setModalVisible={setShowStolenModal}
                description={translate('setStolenDisableCamera')}
                title={translate('disable')}
                customButtonLabel={translate('continue')}
                type='warning'
                onSubmit={save}
            />

            <AreaInvasionModal
                isModalVisible={isDrawingAreaModalVisible}
                setIsModalVisible={setIsDrawingAreaModalVisible}
                cameraId={route.params?.id}
            />

            {route.params?.id && <PurgeCameraModal
                isModalVisible={purgeCameraModalVisible}
                setModalVisible={setPurgeCameraModalVisible}
                cameraId={route.params?.id}
            />}
        </Container>
    );
}
